import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { GetPeriodSeqListService } from './api-services/get-period-seq-list.service';
import { PeriodSeqComboBoxModel } from './period-seq-combo-box.model';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 受注コード期間マスタコンボボックスを制御するクラス
 *
 * @export PeriodSeqComboBoxComponent
 * @class PeriodSeqComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-period-seq-combo-box',
  templateUrl: 'period-seq-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PeriodSeqComboBoxComponent,
      multi: true
    },
    {
      provide: GetPeriodSeqListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetPeriodSeqListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }
  ],
})

export class PeriodSeqComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
  public selectedPeriodSeq: string = <string>'';
  public selectedPeriodTitle: string = <string>'';
  public selectedReceiptSOrderCode: string = <string>'';
  public selectedReceiptEOrderCode: string = <string>'';
  public selectedMediumType: string = <string>'';
  public selectedIndex: number = <number>-1;                                    // 選択した番号

  public model: PeriodSeqComboBoxModel = new PeriodSeqComboBoxModel();

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() public tabindex: number = null;

  // レイアウト系の変数
  @Input() left: number = <number>0;
  @Input() top: number = <number>0;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() disabled: boolean = <boolean>false;
  @Input() required: boolean = <boolean>false;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid
  @Input() afterFocus: any = null;                                              // ロード完了後フォーカスを移動させるid
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Input() forcedEnabledMode: string = null;

  constructor(
    public elementRef: ElementRef,
    public getPeriodSeqListService: GetPeriodSeqListService
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedPeriodSeq) {
      this.selectedPeriodSeq = text;
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期処理
   *
   * @memberof PeriodSeqComboBoxComponent
   */
  ngOnInit(): void {
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof PeriodSeqComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getPeriodSeqListService.execute(this.model);
    }
  }

  /**
   * コンポーネント終了時
   *
   * @memberof PeriodSeqComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof PeriodSeqComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * クリア
   *
   * @memberof PeriodSeqComboBoxComponent
   */
  public clear(): void {
    this.selectedPeriodSeq = '';
    this.selectedPeriodTitle = '';
    this.selectedReceiptSOrderCode = '';
    this.selectedReceiptEOrderCode = '';
    this.selectedMediumType = '';
    this.selectedIndex = -1;
    this.model = new PeriodSeqComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @param {string} codeValue
   * @memberof PeriodSeqComboBoxComponent
   */
  public setCodeValue(codeValue: string): void {
    this.model.setCodeValue = codeValue;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedCodeValue();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof PeriodSeqComboBoxComponent
   */
  public updateSelectedCodeValue(): void {
    this.selectedPeriodSeq = '';
    this.selectedPeriodTitle = '';
    this.selectedReceiptSOrderCode = '';
    this.selectedReceiptEOrderCode = '';
    this.selectedMediumType = '';
    this.selectedIndex = -1;

    const codeList: Array<any> = this.model.codeList;
    for (let i = Number(0); i < codeList.length; i++) {
      if (codeList[i].PeriodTitle === this.model.setCodeValue) {
        this.selectedIndex = i;
      }
    }

    if (this.afterFocus != null) {
      if (this.afterFocus instanceof HTMLElement) {
        this.afterFocus.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.afterFocus.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }
    }

    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * バインドフィールド初期化
   *
   * @private
   * @param {*} e
   * @returns {void}
   * @memberof PeriodSeqComboBoxComponent
   */
  public updateBindedField(e: any): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }

    if (this.selectedIndex === -1) {
      this.selectedPeriodSeq = '';
      this.selectedPeriodTitle = '';
      this.selectedReceiptSOrderCode = '';
      this.selectedReceiptEOrderCode = '';
      this.selectedMediumType = '';

      return;
    }

    this.selectedPeriodSeq = this.model.codeList[this.selectedIndex].PeriodSeq;
    this.selectedPeriodTitle = this.model.codeList[this.selectedIndex].PeriodTitle;
    this.selectedReceiptSOrderCode = this.model.codeList[this.selectedIndex].ReceiptSOrderCode;
    this.selectedReceiptEOrderCode = this.model.codeList[this.selectedIndex].ReceiptEOrderCode;
    this.selectedMediumType = this.model.codeList[this.selectedIndex].MediumType;
  }

}
